import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { TID } from "../../lib/testIds";

const MODELS = [
  {
    tag: "Best for validation",
    title: "Focused Engineering Sprint",
    bullets: [
      "AI use-case validation",
      "Architecture assessment",
      "Technical discovery",
      "Prototype-to-production planning",
      "Modernization assessment",
    ],
    cta: "Discuss a Focused Sprint",
  },
  {
    tag: "Best for building",
    title: "Product Initiative",
    bullets: [
      "New SaaS products",
      "AI feature development",
      "Platform modernization",
      "Workflow automation",
      "Major product modules",
    ],
    cta: "Discuss Your Initiative",
    highlight: true,
  },
  {
    tag: "Best for scaling",
    title: "Dedicated Product Team",
    bullets: [
      "Long-term roadmap delivery",
      "Engineering capacity expansion",
      "Specialized capability",
      "Continuous product development",
    ],
    cta: "Build Your Product Team",
  },
];

export const EngagementModels = () => {
  return (
    <section
      id="engagement"
      data-testid={TID.engagementModels}
      className="relative bg-[#0A0A0A] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              How We Work
            </span>
          </div>
          <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
            Choose the Right Model <span className="text-zinc-500">for the Challenge</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {MODELS.map((m, i) => (
            <motion.div
              key={i}
              data-testid={TID.engagementCard(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`group relative p-8 md:p-10 flex flex-col gap-6 transition-all ${
                m.highlight
                  ? "bg-[#FF5F1F] text-black border border-[#FF5F1F]"
                  : "bg-[#0F0F0F] border border-white/10 hover:border-white/25 text-white"
              }`}
            >
              <div>
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.25em] ${
                    m.highlight ? "text-black/70" : "text-[#FF5F1F]"
                  }`}
                >
                  {m.tag}
                </span>
                <h3 className={`mt-3 font-headings text-2xl md:text-3xl font-bold tracking-tight leading-tight ${m.highlight ? "text-black" : "text-white"}`}>
                  {m.title}
                </h3>
              </div>

              <ul className="flex flex-col gap-3">
                {m.bullets.map((b, bi) => (
                  <li key={bi} className="flex items-start gap-3 text-sm md:text-base">
                    <Check
                      className={`w-4 h-4 mt-1 shrink-0 ${m.highlight ? "text-black" : "text-[#FF5F1F]"}`}
                      strokeWidth={2.5}
                    />
                    <span className={m.highlight ? "text-black/90" : "text-zinc-300"}>{b}</span>
                  </li>
                ))}
              </ul>

              <div className={`mt-auto pt-6 border-t ${m.highlight ? "border-black/20" : "border-white/10"}`}>
                <span className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] ${m.highlight ? "text-black" : "text-white"} group-hover:gap-3 transition-all`}>
                  {m.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
